import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';

interface CreateEventTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preSelectedSubcommitteeId?: string;
}

interface SubcommitteeOption {
  id: string;
  name: string;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface EventOption {
  id: string;
  name: string;
}

interface WeekendTaskRoleOption {
  id: string;
  name: string;
}

interface ApiError {
  message?: string | string[];
  details?: string[];
  statusCode?: number;
}

const CATEGORY_OPTIONS = ['Weekend', 'Planning', 'General', 'Other'] as const;
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'] as const;

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    position: 'relative' as const,
  },
  panelMobile: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    width: '100%',
    maxWidth: '100vw',
    maxHeight: '95vh',
    overflowY: 'auto' as const,
    position: 'relative' as const,
    margin: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.88rem',
    color: '#111827',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.88rem',
    color: '#111827',
    minHeight: '80px',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.88rem',
    color: '#111827',
    backgroundColor: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
  },
  row: {
    display: 'flex',
    gap: '12px',
  },
  rowMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0',
  },
  halfField: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '0.88rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '0.88rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#9ca3af',
    color: '#fff',
    fontSize: '0.88rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '0.8rem',
    marginTop: '4px',
  },
  apiError: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    padding: '10px 14px',
    color: '#dc2626',
    fontSize: '0.84rem',
    marginBottom: '16px',
  },
  charCount: {
    fontSize: '0.72rem',
    color: '#9ca3af',
    textAlign: 'right' as const,
    marginTop: '2px',
  },
};

export function CreateEventTaskModal({ isOpen, onClose, onSuccess, preSelectedSubcommitteeId }: CreateEventTaskModalProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('');
  const [eventId, setEventId] = useState('');
  const [subcommitteeId, setSubcommitteeId] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<string>('Medium');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dropdown data
  const [subcommittees, setSubcommittees] = useState<SubcommitteeOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [weekendTaskRoles, setWeekendTaskRoles] = useState<WeekendTaskRoleOption[]>([]);
  const [assigneeRoleMap, setAssigneeRoleMap] = useState<Record<string, string>>({});
  const [loadingMembers, setLoadingMembers] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      apiClient<SubcommitteeOption[]>('/subcommittees')
        .then(setSubcommittees)
        .catch(() => setSubcommittees([]));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      apiClient<EventOption[]>('/events')
        .then(setEvents)
        .catch(() => setEvents([]));
      apiClient<WeekendTaskRoleOption[]>('/weekend-task-roles')
        .then(setWeekendTaskRoles)
        .catch(() => setWeekendTaskRoles([]));
    }
  }, [isOpen]);

  // Fetch members when subcommittee changes
  useEffect(() => {
    if (!subcommitteeId) {
      setMembers([]);
      setAssigneeIds([]);
      return;
    }
    setLoadingMembers(true);
    setAssigneeIds([]);
    apiClient<Array<{ member: MemberOption }>>(
      `/subcommittees/${subcommitteeId}/affiliations`,
    )
      .then((data) => {
        if (Array.isArray(data)) {
          const memberList = data.map((a) => a.member).filter(Boolean);
          setMembers(memberList);
        } else {
          setMembers([]);
        }
      })
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [subcommitteeId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setCategory('Weekend');
      setEventId('');
      setSubcommitteeId(preSelectedSubcommitteeId || '');
      setAssigneeIds([]);
      setDueDate('');
      setPriority('Medium');
      setErrors({});
      setApiError('');
      setSubmitting(false);
      setMembers([]);
      setLoadingMembers(false);
    }
  }, [isOpen, preSelectedSubcommitteeId]);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or less';
    }

    if (!subcommitteeId) {
      newErrors.subcommitteeId = 'Subcommittee is required';
    }

    if (!category) {
      newErrors.category = 'Category is required';
    }

    if (!assigneeIds.length) {
      newErrors.assigneeIds = 'At least one assignee is required';
    }

    if (!dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    if (category === 'Weekend' && !eventId) {
      newErrors.eventId = 'Event is required for Weekend category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, description, subcommitteeId, assigneeIds, dueDate, category, eventId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim(),
        category,
        subcommitteeId,
        assigneeIds,
        dueDate: new Date(dueDate).toISOString(),
        priority,
      };

      if (category === 'Weekend' && eventId) {
        payload.eventId = eventId;
      }

      await apiClient('/event-tasks', {
        method: 'POST',
        body: payload,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as ApiError;
      let message: string;
      if (error?.details && Array.isArray(error.details)) {
        message = error.details.join(', ');
      } else if (Array.isArray(error?.message)) {
        message = error.message.join(', ');
      } else {
        message = error?.message || 'Failed to create task. Please try again.';
      }
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-event-task-modal-title"
      onKeyDown={handleKeyDown}
    >
      <div
        ref={contentRef}
        style={isMobile ? styles.panelMobile : styles.panel}
        tabIndex={-1}
      >
        <div style={styles.header}>
          <h3 id="create-event-task-modal-title" style={styles.title}>
            Create Event Task
          </h3>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            ✕
          </button>
        </div>

        {apiError && <div style={styles.apiError}>{apiError}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {/* Title */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="task-title">
              Title <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              id="task-title"
              type="text"
              style={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Enter task title"
            />
            <div style={styles.charCount}>{title.length}/200</div>
            {errors.title && <div style={styles.errorText}>{errors.title}</div>}
          </div>

          {/* Description */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="task-description">
              Description <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              id="task-description"
              style={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              placeholder="Enter task description"
            />
            <div style={styles.charCount}>{description.length}/2000</div>
            {errors.description && <div style={styles.errorText}>{errors.description}</div>}
          </div>

          {/* Category and Priority row */}
          <div style={isMobile ? styles.rowMobile : styles.row}>
            <div style={{ ...styles.formGroup, ...styles.halfField }}>
              <label style={styles.label} htmlFor="task-category">
                Category <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                id="task-category"
                style={styles.select}
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  if (e.target.value !== 'Weekend') {
                    setEventId('');
                  }
                }}
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errors.category && <div style={styles.errorText}>{errors.category}</div>}
            </div>

            <div style={{ ...styles.formGroup, ...styles.halfField }}>
              <label style={styles.label} htmlFor="task-priority">
                Priority
              </label>
              <select
                id="task-priority"
                style={styles.select}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Event dropdown - only shown when category is Weekend */}
          {category === 'Weekend' && (
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="task-event">
                Event <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                id="task-event"
                style={styles.select}
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              >
                <option value="">Select an event</option>
                {events.map((evt) => (
                  <option key={evt.id} value={evt.id}>
                    {evt.name}
                  </option>
                ))}
              </select>
              {errors.eventId && <div style={styles.errorText}>{errors.eventId}</div>}
            </div>
          )}

          {/* Subcommittee */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="task-subcommittee">
              Subcommittee <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select
              id="task-subcommittee"
              style={styles.select}
              value={subcommitteeId}
              onChange={(e) => setSubcommitteeId(e.target.value)}
            >
              <option value="">Select a subcommittee</option>
              {subcommittees.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name}
                </option>
              ))}
            </select>
            {errors.subcommitteeId && (
              <div style={styles.errorText}>{errors.subcommitteeId}</div>
            )}
          </div>

          {/* Assignees with Role picker */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Assignees <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <div style={{
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              maxHeight: '200px',
              overflowY: 'auto' as const,
              padding: '6px',
              backgroundColor: !subcommitteeId || loadingMembers ? '#f3f4f6' : '#fff',
            }}>
              {!subcommitteeId ? (
                <div style={{ fontSize: '0.84rem', color: '#9ca3af', padding: '4px 6px' }}>
                  Select a subcommittee first
                </div>
              ) : loadingMembers ? (
                <div style={{ fontSize: '0.84rem', color: '#9ca3af', padding: '4px 6px' }}>
                  Loading members...
                </div>
              ) : members.length === 0 ? (
                <div style={{ fontSize: '0.84rem', color: '#9ca3af', padding: '4px 6px' }}>
                  No members found
                </div>
              ) : (
                members.map((m) => {
                  const isSelected = assigneeIds.includes(m.id);
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 6px',
                        fontSize: '0.86rem',
                        borderRadius: '4px',
                        marginBottom: '2px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssigneeIds((prev) => [...prev, m.id]);
                            const defaultRole = weekendTaskRoles.length > 0 ? weekendTaskRoles[0].id : '';
                            setAssigneeRoleMap((prev) => ({ ...prev, [m.id]: defaultRole }));
                          } else {
                            setAssigneeIds((prev) => prev.filter((id) => id !== m.id));
                            setAssigneeRoleMap((prev) => {
                              const next = { ...prev };
                              delete next[m.id];
                              return next;
                            });
                          }
                        }}
                        aria-label={`Select ${m.firstName} ${m.lastName}`}
                      />
                      <span style={{ minWidth: '110px' }}>{m.firstName} {m.lastName}</span>
                      {isSelected && weekendTaskRoles.length > 0 && (
                        <select
                          value={assigneeRoleMap[m.id] || ''}
                          onChange={(e) =>
                            setAssigneeRoleMap((prev) => ({ ...prev, [m.id]: e.target.value }))
                          }
                          style={{
                            fontSize: '0.78rem',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid #d1d5db',
                            flex: 1,
                            maxWidth: '180px',
                          }}
                          aria-label={`Role for ${m.firstName} ${m.lastName}`}
                        >
                          <option value="">No role</option>
                          {weekendTaskRoles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {assigneeIds.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                {assigneeIds.length} selected
              </div>
            )}
            {errors.assigneeIds && <div style={styles.errorText}>{errors.assigneeIds}</div>}
          </div>

          {/* Due Date */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="task-due-date">
              Due Date <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              id="task-due-date"
              type="date"
              style={styles.input}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            {errors.dueDate && <div style={styles.errorText}>{errors.dueDate}</div>}
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={submitting ? styles.submitBtnDisabled : styles.submitBtn}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
